// hooks/useRouteCalculations.js
import { useMemo } from 'react';
import { 
  extractRouteTimeData, 
  calculateRouteDuration,
  calculateRouteFinishTime,
  calculateStopTimes
} from '../utils/routeTimeUtils';
import { 
  processRouteStops, 
  calculateRouteMetrics,
  validateRouteData,
  needsReoptimization
} from '../utils/routeDataUtils';
import { 
  generateTimelineItems,
  calculateTimelineBounds,
  calculateTimelineGroups
} from '../utils/timelineUtils';

/**
 * Custom hook for memoized route calculations
 * @param {Object} route - Route object
 * @param {Date} selectedDate - Selected date
 * @param {Array} sites - Array of all sites
 * @param {Array} crews - Array of crews
 * @param {string} activeMarkerId - Currently active marker ID
 * @returns {Object} Calculated route data
 */
export const useRouteCalculations = (route, selectedDate, sites, crews = [], activeMarkerId = null) => {
  // Memoized time data extraction
  const timeData = useMemo(() => {
    if (!route) return null;
    return extractRouteTimeData(route);
  }, [route]);

  // Memoized stop processing
  const processedStops = useMemo(() => {
    if (!route || !sites) return [];
    return processRouteStops(route, sites);
  }, [route, sites]);

  // Memoized timeline items generation
  const timelineItems = useMemo(() => {
    if (!route || !processedStops || processedStops.length === 0) return [];
    return generateTimelineItems(route, processedStops, selectedDate, crews, activeMarkerId);
  }, [route, processedStops, selectedDate, crews, activeMarkerId]);

  // Memoized route metrics
  const metrics = useMemo(() => {
    if (!route) return null;
    return calculateRouteMetrics(route);
  }, [route]);

  // Memoized route duration
  const duration = useMemo(() => {
    if (!route) return null;
    return calculateRouteDuration(route);
  }, [route]);

  // Memoized finish time
  const finishTime = useMemo(() => {
    if (!route || !selectedDate) return null;
    return calculateRouteFinishTime(route, selectedDate);
  }, [route, selectedDate]);

  // Memoized validation result
  const validation = useMemo(() => {
    return validateRouteData(route);
  }, [route]);

  // Check if needs re-optimization
  const needsOptimization = useMemo(() => {
    if (!route) return false;
    return needsReoptimization(route);
  }, [route]);

  return {
    timeData,
    processedStops,
    timelineItems,
    metrics,
    duration,
    finishTime,
    validation,
    needsOptimization
  };
};

/**
 * Custom hook for multiple routes calculations
 * @param {Array} routes - Array of routes
 * @param {Date} selectedDate - Selected date
 * @param {Array} sites - Array of all sites
 * @param {Array} crews - Array of crews
 * @param {string} activeMarkerId - Currently active marker ID
 * @returns {Object} Calculated data for all routes
 */
export const useMultipleRoutesCalculations = (routes, selectedDate, sites, crews = [], activeMarkerId = null) => {
  // Memoized timeline groups
  const timelineGroups = useMemo(() => {
    return calculateTimelineGroups(crews);
  }, [crews]);

  // Memoized timeline items for all routes
  const allTimelineItems = useMemo(() => {
    if (!routes || routes.length === 0) return [];
    
    const items = [];
    routes.forEach(route => {
      const processedStops = processRouteStops(route, sites);
      const routeItems = generateTimelineItems(route, processedStops, selectedDate, crews, activeMarkerId);
      items.push(...routeItems);
    });
    
    return items;
  }, [routes, selectedDate, sites, crews, activeMarkerId]);

  // Memoized timeline bounds
  const timelineBounds = useMemo(() => {
    return calculateTimelineBounds(allTimelineItems, selectedDate);
  }, [allTimelineItems, selectedDate]);

  // Memoized total metrics
  const totalMetrics = useMemo(() => {
    if (!routes || routes.length === 0) {
      return {
        totalStops: 0,
        totalServiceHours: 0,
        totalDriveHours: 0,
        totalHours: 0,
        averageEfficiency: 0,
        totalMiles: 0
      };
    }

    const totals = routes.reduce((acc, route) => {
      const metrics = calculateRouteMetrics(route);
      acc.totalStops += metrics.stopCount;
      acc.totalServiceHours += metrics.serviceHours;
      acc.totalDriveHours += metrics.driveHours;
      acc.totalHours += metrics.totalHours;
      acc.efficiencySum += metrics.efficiency;
      acc.totalMiles += metrics.milesDriven;
      return acc;
    }, {
      totalStops: 0,
      totalServiceHours: 0,
      totalDriveHours: 0,
      totalHours: 0,
      efficiencySum: 0,
      totalMiles: 0
    });

    return {
      ...totals,
      averageEfficiency: routes.length > 0 ? Math.round(totals.efficiencySum / routes.length) : 0
    };
  }, [routes]);

  return {
    timelineGroups,
    allTimelineItems,
    timelineBounds,
    totalMetrics
  };
};

/**
 * Custom hook for route metrics calculations
 * @param {Array} routes - Array of routes
 * @returns {Object} Route metrics by crew
 */
export const useRouteMetrics = (routes) => {
  return useMemo(() => {
    if (!routes || routes.length === 0) return {};

    const metricsByCrew = {};

    routes.forEach(route => {
      const metrics = calculateRouteMetrics(route);
      
      if (!metricsByCrew[route.crewId]) {
        metricsByCrew[route.crewId] = {
          routes: 0,
          totalStops: 0,
          totalHours: 0,
          serviceHours: 0,
          driveHours: 0,
          totalMiles: 0,
          averageEfficiency: 0
        };
      }

      const crewMetrics = metricsByCrew[route.crewId];
      crewMetrics.routes++;
      crewMetrics.totalStops += metrics.stopCount;
      crewMetrics.totalHours += metrics.totalHours;
      crewMetrics.serviceHours += metrics.serviceHours;
      crewMetrics.driveHours += metrics.driveHours;
      crewMetrics.totalMiles += metrics.milesDriven;
      crewMetrics.averageEfficiency = 
        ((crewMetrics.averageEfficiency * (crewMetrics.routes - 1)) + metrics.efficiency) / crewMetrics.routes;
    });

    return metricsByCrew;
  }, [routes]);
};

/**
 * Custom hook for crew workload calculations
 * @param {string} crewId - Crew ID
 * @param {Array} weeklyRoutes - Weekly routes array
 * @returns {Object} Crew workload data
 */
export const useCrewWorkload = (crewId, weeklyRoutes) => {
  return useMemo(() => {
    if (!crewId || !weeklyRoutes) {
      return {
        dailyHours: {},
        weeklyTotal: 0,
        averageDaily: 0,
        peakDay: null,
        lightestDay: null
      };
    }

    const dailyHours = {};
    let weeklyTotal = 0;
    let peakDay = { day: null, hours: 0 };
    let lightestDay = { day: null, hours: Infinity };

    weeklyRoutes.forEach(dayRoute => {
      const dayName = dayRoute.day;
      const crewRoute = dayRoute.routes?.find(r => r.crewId === crewId);
      
      if (crewRoute) {
        const metrics = calculateRouteMetrics(crewRoute);
        dailyHours[dayName] = metrics.totalHours;
        weeklyTotal += metrics.totalHours;

        if (metrics.totalHours > peakDay.hours) {
          peakDay = { day: dayName, hours: metrics.totalHours };
        }
        if (metrics.totalHours < lightestDay.hours) {
          lightestDay = { day: dayName, hours: metrics.totalHours };
        }
      } else {
        dailyHours[dayName] = 0;
        if (0 < lightestDay.hours) {
          lightestDay = { day: dayName, hours: 0 };
        }
      }
    });

    const workDays = Object.values(dailyHours).filter(hours => hours > 0).length;
    const averageDaily = workDays > 0 ? weeklyTotal / workDays : 0;

    return {
      dailyHours,
      weeklyTotal: Math.round(weeklyTotal * 10) / 10,
      averageDaily: Math.round(averageDaily * 10) / 10,
      peakDay: peakDay.day ? peakDay : null,
      lightestDay: lightestDay.hours === Infinity ? null : lightestDay
    };
  }, [crewId, weeklyRoutes]);
};

/**
 * Custom hook to check optimization status across routes
 * @param {Array} routes - Array of routes
 * @returns {Object} Optimization status summary
 */
export const useOptimizationStatus = (routes) => {
  return useMemo(() => {
    if (!routes || routes.length === 0) {
      return {
        total: 0,
        optimized: 0,
        needsOptimization: 0,
        percentage: 0,
        routesNeedingOptimization: []
      };
    }

    const status = routes.reduce((acc, route) => {
      acc.total++;
      
      if (route.isOptimized) {
        acc.optimized++;
      }
      
      if (needsReoptimization(route)) {
        acc.needsOptimization++;
        acc.routesNeedingOptimization.push({
          routeId: route._id,
          crewId: route.crewId,
          lastOptimized: route.lastOptimized,
          stopCount: route.stops?.length || 0
        });
      }
      
      return acc;
    }, {
      total: 0,
      optimized: 0,
      needsOptimization: 0,
      routesNeedingOptimization: []
    });

    status.percentage = status.total > 0 
      ? Math.round((status.optimized / status.total) * 100) 
      : 0;

    return status;
  }, [routes]);
};

/**
 * Custom hook for stop time calculations
 * @param {Array} stops - Array of stops
 * @param {Date} selectedDate - Selected date
 * @returns {Array} Stops with calculated times
 */
export const useStopTimeCalculations = (stops, selectedDate) => {
  return useMemo(() => {
    if (!stops || stops.length === 0 || !selectedDate) return [];

    return stops.map(stop => {
      const times = calculateStopTimes(stop, selectedDate);
      return {
        ...stop,
        arrivalMoment: times.arrival,
        departureMoment: times.departure,
        serviceDurationSeconds: times.serviceDuration
      };
    });
  }, [stops, selectedDate]);
};